import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const AuthContext = createContext(null)

// API base URL - use environment variable or default to production API
const API_URL = import.meta.env.VITE_API_URL || 'https://api.ai4solar.com'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Get session token from localStorage
  const getSessionToken = () => localStorage.getItem('ai4solar_session_token')
  const setSessionToken = (token) => {
    if (token) {
      localStorage.setItem('ai4solar_session_token', token)
    } else {
      localStorage.removeItem('ai4solar_session_token')
    }
  }

  // Fetch current user profile
  const fetchUserProfile = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        if (response.status === 401) {
          // Session expired or invalid
          setSessionToken(null)
          setUser(null)
          setSubscription(null)
          return null
        }
        throw new Error('Failed to fetch user profile')
      }

      const data = await response.json()
      if (data.success) {
        setUser(data.user)
        setSubscription(data.subscription)
        return data.user
      }
      return null
    } catch (err) {
      console.error('Error fetching user profile:', err)
      return null
    }
  }, [])

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = getSessionToken()
      if (!token) {
        setLoading(false)
        return
      }

      try {
        // Verify session is still valid
        const response = await fetch(`${API_URL}/api/auth/verify-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: token })
        })

        const data = await response.json()

        if (data.success && data.valid) {
          // Session is valid, fetch user profile
          await fetchUserProfile(token)
        } else {
          // Session invalid, clear it
          setSessionToken(null)
          setUser(null)
          setSubscription(null)
        }
      } catch (err) {
        console.error('Error checking session:', err)
        setSessionToken(null)
      } finally {
        setLoading(false)
      }
    }

    checkSession()
  }, [fetchUserProfile])

  // Login function
  const login = async (email, password) => {
    setError(null)
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email, // API accepts username or email
          password,
          ip_address: '', // Could capture from client
          user_agent: navigator.userAgent
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        const errorMessage = data.error || 'Login failed'
        setError(errorMessage)
        throw new Error(errorMessage)
      }

      // Store session token
      setSessionToken(data.session_token)

      // Set user data
      setUser(data.user)

      // Fetch full profile including subscription
      await fetchUserProfile(data.session_token)

      return { success: true, user: data.user }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Register function (with optional subscription)
  const register = async ({
    email,
    password,
    firstName,
    lastName,
    companyName,
    phoneNumber,
    planId,
    mwAmount,
    paymentMethod
  }) => {
    setError(null)
    try {
      const response = await fetch(`${API_URL}/api/auth/register-with-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          phone_number: phoneNumber,
          plan_id: planId,
          mw_amount: mwAmount,
          payment_method: paymentMethod
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        const errorMessage = data.error || 'Registration failed'
        setError(errorMessage)
        throw new Error(errorMessage)
      }

      // Store session token
      setSessionToken(data.session_token)

      // Set user data
      setUser(data.user)
      setSubscription(data.subscription)

      return {
        success: true,
        user: data.user,
        subscription: data.subscription
      }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Logout function
  const logout = async () => {
    const token = getSessionToken()

    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_token: token })
        })
      } catch (err) {
        console.error('Error during logout:', err)
      }
    }

    // Clear local state
    setSessionToken(null)
    setUser(null)
    setSubscription(null)
    setError(null)
  }

  // Check if email is available
  const checkEmailAvailable = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const data = await response.json()
      return data.success ? data.available : false
    } catch (err) {
      console.error('Error checking email:', err)
      return false
    }
  }

  // Get subscription plans
  const getPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`)
      const data = await response.json()
      return data.success ? data.plans : []
    } catch (err) {
      console.error('Error fetching plans:', err)
      return []
    }
  }

  // Calculate subscription price
  const calculatePrice = async (planId, mwAmount) => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, mw_amount: mwAmount })
      })

      const data = await response.json()
      return data.success ? data.calculation : null
    } catch (err) {
      console.error('Error calculating price:', err)
      return null
    }
  }

  // Create Stripe checkout session
  const createStripeCheckout = async ({
    userId,
    subscriptionId,
    planName,
    amountEur,
    customerEmail
  }) => {
    try {
      const baseUrl = window.location.origin
      const response = await fetch(`${API_URL}/api/payments/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          subscription_id: subscriptionId,
          plan_name: planName,
          amount_eur: amountEur,
          customer_email: customerEmail,
          success_url: `${baseUrl}/register/success`,
          cancel_url: `${baseUrl}/register/cancel`
        })
      })

      const data = await response.json()
      return data
    } catch (err) {
      console.error('Error creating Stripe checkout:', err)
      return { success: false, error: err.message }
    }
  }

  // Create Paysera order
  const createPayseraOrder = async ({
    userId,
    subscriptionId,
    planName,
    amountEur,
    customerEmail,
    customerName
  }) => {
    try {
      const baseUrl = window.location.origin
      const response = await fetch(`${API_URL}/api/payments/paysera/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          subscription_id: subscriptionId,
          plan_name: planName,
          amount_eur: amountEur,
          customer_email: customerEmail,
          customer_name: customerName,
          accept_url: `${baseUrl}/register/success`,
          cancel_url: `${baseUrl}/register/cancel`
        })
      })

      const data = await response.json()
      return data
    } catch (err) {
      console.error('Error creating Paysera order:', err)
      return { success: false, error: err.message }
    }
  }

  // Create bank transfer
  const createBankTransfer = async ({ userId, subscriptionId, amountEur }) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/bank-transfer/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          subscription_id: subscriptionId,
          amount_eur: amountEur
        })
      })

      const data = await response.json()
      return data
    } catch (err) {
      console.error('Error creating bank transfer:', err)
      return { success: false, error: err.message }
    }
  }

  const value = {
    // State
    user,
    subscription,
    loading,
    error,
    isAuthenticated: !!user,

    // Auth functions
    login,
    register,
    logout,
    checkEmailAvailable,

    // Subscription functions
    getPlans,
    calculatePrice,

    // Payment functions
    createStripeCheckout,
    createPayseraOrder,
    createBankTransfer,

    // Session management
    getSessionToken,

    // Utilities
    clearError: () => setError(null)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
